export interface ParsedAddress {
    street_number: string | null;
    street_name: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    formatted_address: string | null;
    place_id?: string;
    geometry?: {
        type: 'Point';
        coordinates: [number, number];
    } | null;
}

interface AddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

interface GooglePlace {
    address_components?: AddressComponent[];
    formatted_address?: string;
    place_id?: string;
    geometry?: {
        location?: {
            lat: () => number;
            lng: () => number;
        };
    };
}

const PROP_MAP: Record<string, keyof ParsedAddress> = {
    street_number: 'street_number',
    route: 'street_name',
    locality: 'city',
    administrative_area_level_1: 'state',
    country: 'country',
    postal_code: 'zip',
};

export const parseAddress = (place: GooglePlace): ParsedAddress => {
    const parsed: ParsedAddress = {
        street_number: null,
        street_name: null,
        city: null,
        state: null,
        zip: null,
        country: null,
        formatted_address: place.formatted_address ?? null,
        place_id: place.place_id,
    };

    for (const comp of place.address_components ?? []) {
        const key = comp.types[0];
        const rodKey = PROP_MAP[key];
        if (rodKey) {
            (parsed[rodKey] as string) = comp.long_name;
        }
    }

    const loc = place.geometry?.location;
    if (loc) {
        parsed.geometry = {
            type: 'Point',
            coordinates: [loc.lng(), loc.lat()],
        };
    }

    return parsed;
};

export const formatAddress = (
    address: Partial<ParsedAddress> & { subpremise?: string | null },
): string => {
    const GROUPS: (keyof typeof address)[][] = [
        ['street_number', 'street_name', 'subpremise'],
        ['city'],
        ['state', 'zip'],
        ['country'],
    ];

    const out: string[] = [];
    for (const group of GROUPS) {
        const parts = group
            .map((k) => (address as Record<string, unknown>)[k as string])
            .filter((v): v is string => Boolean(v && typeof v === 'string'));
        if (parts.length) out.push(parts.join(' '));
    }
    return out.join(', ');
};
